import { useEffect, useState } from 'react';
import {
    collection,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

// Estados predeterminados que siempre estarán disponibles
const DEFAULT_STATUSES = [
    { id: 'pendiente', name: 'Pendiente', color: '#6c757d', emoji: '⏳' },
    { id: 'visitado', name: 'Visitado', color: '#28a745', emoji: '✓' },
];

// Colores disponibles para seleccionar
export const AVAILABLE_COLORS = [
    { value: '#007bff', label: 'Azul' },
    { value: '#17a2b8', label: 'Azul Turquesa' },
    { value: '#28a745', label: 'Verde' },
    { value: '#ffc107', label: 'Amarillo' },
    { value: '#fd7e14', label: 'Naranja' },
    { value: '#dc3545', label: 'Rojo' },
    { value: '#6f42c1', label: 'Púrpura' },
    { value: '#e83e8c', label: 'Rosa' },
    { value: '#20c997', label: 'Verde Azulado' },
    { value: '#6c757d', label: 'Gris' },
];

export const useCustomStatuses = () => {
    const { user } = useContext(AuthContext);
    const [customStatuses, setCustomStatuses] = useState([]);
    const [error, setError] = useState(null);

    // Combinar estados predeterminados con personalizados
    const allStatuses = [...DEFAULT_STATUSES, ...customStatuses];

    useEffect(() => {
        if (!user) {
            setCustomStatuses([]);
            return;
        }

        const colRef = collection(db, 'users', user.uid, 'customStatuses');
        const q = query(colRef, orderBy('createdAt', 'asc'));

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const data = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));
                setCustomStatuses(data);
            },
            (err) => {
                setError(err);
            }
        );

        return () => unsubscribe();
    }, [user]);

    const createStatus = async (name, color, emoji = '📌') => {
        if (!user || !name.trim()) {
            throw new Error('Debes proporcionar un nombre para el estado');
        }

        try {
            // Generar un ID basado en el nombre (slug)
            const statusId = name.toLowerCase()
                .trim()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '');

            // Verificar que no exista ya
            const exists = allStatuses.some(s => s.id === statusId);
            if (exists) {
                throw new Error('Ya existe un estado con ese nombre');
            }

            const docRef = doc(db, 'users', user.uid, 'customStatuses', statusId);
            await setDoc(docRef, {
                name: name.trim(),
                color: color || '#007bff',
                emoji: emoji || '📌',
                createdAt: serverTimestamp(),
            });

            return statusId;
        } catch (err) {
            throw err;
        }
    };

    const deleteStatus = async (statusId) => {
        if (!user) return;

        // No permitir eliminar estados predeterminados
        if (statusId === 'pendiente' || statusId === 'visitado') {
            throw new Error('No se pueden eliminar los estados predeterminados');
        }

        try {
            const docRef = doc(db, 'users', user.uid, 'customStatuses', statusId);
            await deleteDoc(docRef);
        } catch (err) {
            throw err;
        }
    };

    const getStatusById = (statusId) => {
        return allStatuses.find(s => s.id === statusId) || DEFAULT_STATUSES[0];
    };

    return {
        allStatuses,
        customStatuses,
        createStatus,
        deleteStatus,
        getStatusById,
        error,
    };
};

